import { useRef, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import cx from 'classnames';

import { STATUSES } from '@openclassrooms/ui-web/learning-activities';
import { translate, getActiveLang } from '@openclassrooms/translations';
import Typography from '@classify/react-core-components/Typography';

import { QuizLearningStatus } from '../LearningStatus';
import { QUIZ_COMPLETED_STATUSES } from '../../constants/LearningStatuses';

const { STATUS_CAN_RETRY, STATUS_FAILED, STATUS_PASSED } = STATUSES;

interface Props {
  quizLearningStatus: (typeof QUIZ_COMPLETED_STATUSES)[number];
  quizReinitializationDate?: string;
}

const useStyles = makeStyles(({ spacing, breakpoints, palette }) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: spacing(0),
    [breakpoints.up('sm')]: {
      paddingLeft: spacing(2),
    },

    [breakpoints.up('lg')]: {
      paddingLeft: spacing(10),
    },
  },
  iconContainer: {
    display: 'none',
    [breakpoints.up('sm')]: {
      display: 'block',
      padding: spacing(2, 0, 2, 2),
    },
  },
  content: {
    minWidth: 0,
    padding: spacing(2),
    color: palette.text.onMain,
    '& a': {
      color: 'inherit',
    },
  },
  [STATUS_FAILED]: {
    backgroundColor: palette.notification.warning.background,
  },
  [STATUS_CAN_RETRY]: {
    backgroundColor: palette.notification.info.background,
  },
  [STATUS_PASSED]: {
    backgroundColor: palette.notification.valid.background,
  },
}));

const QuizCompletionBanner = ({ quizLearningStatus, quizReinitializationDate }: Props) => {
  const classes = useStyles();
  const bannerRef = useRef<HTMLDivElement>(null);
  const locale = getActiveLang() === 'fr' ? 'fr' : 'en-US';
  const formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeStyle: 'short' });

  const quizBannerConfig = {
    [STATUS_CAN_RETRY]: {
      title: translate('course.chapter.quiz.to_retake.ready.status'),
      description: translate('course.chapter.quiz.to_retake.ready.desc'),
    },
    [STATUS_FAILED]: {
      title: translate('course.chapter.quiz.to_retake.wait.status'),
      description:
        quizReinitializationDate &&
        translate('course.chapter.quiz.to_retake_date.wait.desc', {
          date: formatter.format(new Date(quizReinitializationDate)),
        }),
    },
    [STATUS_PASSED]: {
      title: translate('course.chapter.quiz.successfully_completed.status'),
      description: '',
    },
  };

  useEffect(() => {
    if (bannerRef.current) {
      bannerRef.current?.focus();
    }
  }, [bannerRef]);

  if (!quizBannerConfig[quizLearningStatus]) {
    return null;
  }

  const { title, description } = quizBannerConfig[quizLearningStatus];

  return (
    <div className={cx(classes.root, classes[quizLearningStatus])} tabIndex={-1} ref={bannerRef}>
      <div className={classes.iconContainer}>
        <QuizLearningStatus status={quizLearningStatus} ariaHidden />
      </div>
      <div className={classes.content}>
        <Typography variant="subheading">{title}</Typography>
        {description && <Typography sx={{ marginTop: 1 }}>{description}</Typography>}
      </div>
    </div>
  );
};

export default QuizCompletionBanner;
